import { css } from '@emotion/react';
import { px2rem } from '@workday/canvas-kit-styling';
import { system } from '@workday/canvas-tokens-web';
import { WcpStyles } from '@workday/wcp-tokens';

/**
 * FFF Acid Grotesk is a Workday marketing/brand font (NOT for general Devsite use).
 * Scoped to logged-out marketing surfaces (About, Docs, APIs when logged out).
 * i18n note: covers Latin characters only — add a supplemental font stack for non-Latin locales.
 * Add new @font-face blocks here as Figma specs call for additional weights.
 */
const acidGroteskCdn = (file: string) => `https://static.workday.com/fonts/Acid_Grotesk/WOFF2/${file}.woff2`;

export const globals = css`
  @font-face {
    font-family: 'FFF Acid Grotesk';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url('${acidGroteskCdn('FFF-AcidGrotesk-Regular')}') format('woff2');
  }

  @font-face {
    font-family: 'FFF Acid Grotesk';
    font-style: normal;
    font-weight: 800;
    font-display: swap;
    src: url('${acidGroteskCdn('FFF-AcidGrotesk-ExtraBold')}') format('woff2');
  }

  *,
  ::before,
  ::after {
    -webkit-box-sizing: inherit;
    box-sizing: inherit;
  }

  :target:before {
    content: '';
    display: block;
    height: ${px2rem(25)}; /* fixed header height*/
    margin: ${px2rem(-25)} 0 0; /* negative fixed header height */
  }

  body {
    background: ${WcpStyles.color.bg.default} !important;
    box-sizing: border-box;
    color: ${WcpStyles.color.text.default};
    font-family: 'Roboto', 'Helvetica Neue', 'Helvetica', Arial, sans-serif;
    font-size: var(${system.type.subtext.large.fontSize});
    font-weight: var(${system.type.subtext.large.fontWeight});
    letter-spacing: var(${system.type.subtext.large.letterSpacing});
    line-height: var(${system.type.subtext.large.lineHeight});
    margin: 0;
    padding: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1 {
    color: ${WcpStyles.color.text.strong};
    font-size: var(${system.type.heading.medium.fontSize});
    font-weight: var(${system.type.heading.medium.fontWeight});
    line-height: var(${system.type.heading.medium.lineHeight});
  }

  h2 {
    color: ${WcpStyles.color.text.strong};
    line-height: ${px2rem(26)};
  }

  h4 {
    color: ${WcpStyles.color.text.strong};
    font-size: var(${system.type.body.small.fontSize});
    font-weight: var(${system.type.body.small.fontWeight});
    letter-spacing: var(${system.type.body.small.letterSpacing});
    line-height: var(${system.type.body.small.lineHeight});
  }

  h5 {
    font-size: var(${system.type.body.small.fontSize});
    font-weight: var(${system.type.body.small.fontWeight});
    letter-spacing: var(${system.type.body.small.letterSpacing});
    line-height: var(${system.type.body.small.lineHeight});
  }

  a {
    color: ${WcpStyles.color.static.blue.default};
    text-decoration: none;
    cursor: pointer;
  }

  a,
  button {
    &[aria-disabled='true'],
    &[aria-disabled='true']:active,
    &[aria-disabled='true']:focus,
    &[aria-disabled='true']:hover {
      cursor: not-allowed;
      opacity: 0.4;
    }
  }

  .sr-only {
    clip: rect(${px2rem(1)}, ${px2rem(1)}, ${px2rem(1)}, ${px2rem(1)});
    clip-path: polygon(0 0, 0 0, 0 0, 0 0);
    position: absolute;
    overflow: hidden;
    white-space: nowrap;
    height: ${px2rem(1)};
    width: ${px2rem(1)};
    margin: ${px2rem(-1)};
    padding: 0;
    border: 0;
  }
`;
